import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Save,
  ShieldCheck,
  Lock,
  RotateCcw,
  CheckSquare,
  Square,
  Info,
} from "lucide-react";
import { toast } from "sonner";

/* ---------------------------------------------------------------
   Menu label mapping (must match keys in AdminDashboard NAV)
   --------------------------------------------------------------- */
const MENU_LABELS = {
  "hero-slides": "Hero Slides",
  "hero-stats": "Hero Stats",
  "about-content": "Tentang Kami — Konten",
  "about-pillars": "Tentang Kami — Pilar",
  services: "Layanan",
  doctors: "Dokter",
  news: "Berita",
  gallery: "Galeri",
  laporan: "Laporan",
  "site-content": "Konten Profil",
  splash: "Splash Pengumuman",
  contacts: "Pesan Kontak",
  users: "User Admin",
  "audit-logs": "Audit Log",
  artifacts: "Download Artifacts",
  "role-management": "Manajemen Role",
  maintenance: "Maintenance Mode",
};

const RoleManagementPanel = () => {
  const { authAxios, user } = useAuth();
  const isSuperadmin = user && user.role === "superadmin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [knownMenus, setKnownMenus] = useState([]);
  const [lockedMenus, setLockedMenus] = useState([]);
  const [defaultAdmin, setDefaultAdmin] = useState([]);
  const [adminMenus, setAdminMenus] = useState(new Set());
  const [dirty, setDirty] = useState(false);

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/role-permissions")
      .then((r) => {
        const data = r.data || {};
        setKnownMenus(data.known_menus || []);
        setLockedMenus(data.locked_superadmin_menus || []);
        setDefaultAdmin(data.default_admin_menus || []);
        setAdminMenus(new Set((data.permissions && data.permissions.admin) || []));
        setDirty(false);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Gagal memuat permissions");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperadmin) load();
  }, [isSuperadmin]);

  const lockedSet = useMemo(() => new Set(lockedMenus), [lockedMenus]);

  const toggle = (menu) => {
    if (lockedSet.has(menu)) return; // cannot toggle locked
    setAdminMenus((prev) => {
      const next = new Set(prev);
      if (next.has(menu)) next.delete(menu);
      else next.add(menu);
      return next;
    });
    setDirty(true);
  };

  const selectAll = () => {
    const next = new Set(knownMenus.filter((m) => !lockedSet.has(m)));
    setAdminMenus(next);
    setDirty(true);
  };

  const clearAll = () => {
    setAdminMenus(new Set());
    setDirty(true);
  };

  const resetToDefault = () => {
    setAdminMenus(new Set(defaultAdmin));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put("/admin/role-permissions", {
        admin: Array.from(adminMenus),
      });
      toast.success("Permissions berhasil disimpan");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div
        data-testid="admin-role-mgmt-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">
          Manajemen Role hanya dapat diakses oleh <b>superadmin</b>.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-role-management" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-blue-800" />
            Manajemen Role
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Atur akses menu untuk role <b>admin</b>. Menu keamanan tinggi
            (User Admin, Audit Log, Artifacts, Manajemen Role) selalu terkunci
            untuk <b>superadmin</b>.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="role-mgmt-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-blue-800 hover:bg-blue-900 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Simpan Perubahan
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={selectAll}
          data-testid="role-mgmt-select-all"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <CheckSquare className="h-3.5 w-3.5" />
          Pilih Semua (yang bisa)
        </button>
        <button
          type="button"
          onClick={clearAll}
          data-testid="role-mgmt-clear-all"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <Square className="h-3.5 w-3.5" />
          Hapus Semua
        </button>
        <button
          type="button"
          onClick={resetToDefault}
          data-testid="role-mgmt-reset-default"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset ke Default
        </button>
        {dirty && (
          <span className="ml-auto px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-xs">
            Belum disimpan
          </span>
        )}
      </div>

      {/* Info panel */}
      <div className="p-4 rounded-2xl bg-blue-50 border border-blue-100 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900 leading-relaxed">
          Menu yang <b>dicentang</b> akan tampil di sidebar untuk user berperan{" "}
          <b>admin</b>. Perubahan berlaku otomatis setelah user admin{" "}
          <b>refresh halaman / login ulang</b>. Menu dengan ikon{" "}
          <Lock className="inline h-3 w-3" /> tidak dapat diberikan ke admin
          karena berisi kontrol keamanan sensitif.
        </div>
      </div>

      {/* Matrix table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="grid grid-cols-[1fr,120px,120px] items-center px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs font-heading font-semibold text-slate-700 uppercase tracking-wider">
          <div>Menu</div>
          <div className="text-center">Admin</div>
          <div className="text-center">Superadmin</div>
        </div>
        <div className="divide-y divide-slate-100">
          {knownMenus.map((menu) => {
            const locked = lockedSet.has(menu);
            const hasAdmin = adminMenus.has(menu);
            return (
              <div
                key={menu}
                data-testid={`role-mgmt-row-${menu}`}
                className={`grid grid-cols-[1fr,120px,120px] items-center px-5 py-3 hover:bg-slate-50/60 transition ${
                  locked ? "bg-slate-50/40" : ""
                }`}
              >
                <div>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {locked && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                    {MENU_LABELS[menu] || menu}
                  </div>
                  <div className="text-xs text-slate-400 font-mono">{menu}</div>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => toggle(menu)}
                    disabled={locked}
                    data-testid={`role-mgmt-toggle-admin-${menu}`}
                    className={`h-6 w-11 rounded-full transition relative ${
                      locked
                        ? "bg-slate-200 cursor-not-allowed"
                        : hasAdmin
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-slate-300 hover:bg-slate-400"
                    }`}
                    title={locked ? "Menu ini terkunci untuk superadmin" : ""}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                        hasAdmin && !locked ? "left-5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
                <div className="flex justify-center">
                  <div className="h-6 w-11 rounded-full bg-purple-600 relative">
                    <span className="absolute top-0.5 left-5 h-5 w-5 rounded-full bg-white shadow" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RoleManagementPanel;
